/**
 * Sentry initialisation for cert-web-frontend.
 *
 * This file MUST be the first import in main.jsx so Sentry can instrument
 * the application before any other code runs.
 *
 * Environment variables (set at build time by Vite):
 *   VITE_SENTRY_DSN          – DSN from your self-hosted Sentry project
 *   VITE_SENTRY_ENVIRONMENT  – "development" | "staging" | "production"
 *   VITE_SENTRY_RELEASE      – git commit SHA injected by CI (optional locally)
 *   VITE_SENTRY_TUNNEL       – Django proxy URL, bypasses browser TLS cert errors
 *                               (required when Sentry uses a self-signed certificate)
 *
 * Behaviour per environment:
 *   development  → debug logs ON, sendDefaultPii ON, trace 100% of requests
 *   staging      → debug logs OFF, sendDefaultPii OFF, trace 20% of requests
 *   production   → debug logs OFF, sendDefaultPii OFF, trace 10% of requests
 */
import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

// Tunnel URL: routes all Sentry envelopes through your own Django backend.
// This is REQUIRED when Sentry uses a self-signed TLS certificate because
// browsers cannot be told to skip certificate verification in JavaScript.
// It also prevents ad-blockers from blocking Sentry events in production.
// We resolve the tunnel path dynamically from VITE_APP_API_URL so that it
// automatically works in development, staging, and production.
let tunnel = import.meta.env.VITE_SENTRY_TUNNEL || undefined;
if (!tunnel) {
  const apiUrl = import.meta.env.VITE_APP_API_URL;
  if (apiUrl) {
    const baseApi = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    tunnel = baseApi.startsWith('http') ? `${baseApi}/api/sentry-tunnel/` : '/api/sentry-tunnel/';
  } else {
    tunnel = '/api/sentry-tunnel/';
  }
}

// Only initialise when a DSN is provided.
// In local development this stays a no-op unless you explicitly set the var.
if (dsn) {
  Sentry.init({
    dsn,
    environment,

    // Tag each event with the release built into the image by CI.
    // Vite inlines VITE_SENTRY_RELEASE as the git commit SHA.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

    // Route envelopes through the Django proxy instead of hitting the Sentry
    // host directly. Required for self-signed TLS certificates.
    // Falls back to undefined (direct) when not set, e.g. in production with a
    // valid cert where browsers can reach Sentry without the proxy.
    tunnel,

    integrations: [
      // Automatic page-load and navigation performance tracing
      Sentry.browserTracingIntegration(),
      // Session Replay — captures a video-like replay when an error occurs.
      // replaysSessionSampleRate: 0  → never record normal sessions (privacy)
      // replaysOnErrorSampleRate: 1  → always record the session that crashed
      Sentry.replayIntegration({
        // Mask all text and block all media by default for user privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance tracing:
    //   development → 100% so every request is visible while testing
    //   staging     → 20% to catch real traffic without too much noise
    //   production  → 10% to keep volume manageable
    tracesSampleRate:
      environment === 'development' ? 1.0
      : environment === 'staging'   ? 0.2
      : 0.1,

    // Session Replay sampling (see comment above)
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,

    // Ignore noisy, non-actionable errors
    ignoreErrors: [
      // Browser extension noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors from user going offline
      'NetworkError',
      'Failed to fetch',
      'Load failed',
    ],

    // Send request headers, IP, and user data across all environments.
    sendDefaultPii: true,

    // Print internal Sentry logs to the browser console only in development.
    // Never enable this in staging/production — it is very noisy.
    debug: environment === 'development',

    // Only allow events from your own origin (blocks third-party script errors)
    allowUrls: [
      /https?:\/\/ethiocert\.et/,
      /https?:\/\/ethiocert\.insa\.gov\.et/,
      /https?:\/\/www\.ethiocert\.gov\.et/,
      /https?:\/\/localhost/,
      /https?:\/\/127\.0\.0\.1/,
    ],
  });

  // Tell the Sentry ingest server to infer the browser's real IP from the
  // incoming HTTP request — uses the special '{{auto}}' token resolved server-side.
  Sentry.setUser({ ip_address: '{{auto}}' });

  // In development, expose Sentry on window for console testing:
  //   window.Sentry.captureMessage('test', 'error')
  //   window.Sentry.captureException(new Error('test'))
  if (environment === 'development') {
    window.Sentry = Sentry;
  }
}

export default Sentry;
